import React from 'react';

import type { VideoMediaStrategy } from '../VideoRecorder';
import Button from '../Input2/Button';
import Textarea from '../Input2/Textarea';
import { ESC_PRIORITY, useEscStack } from '../contexts/EscCancelable.context';
import { StepIconSvg } from '../StepIcon';
import { General, Images } from '../StrokeIcons';
import { getTextFromHtml } from '../utils/htmlUtils';
import { VideoRecorder } from '../VideoRecorder';

export type VideoNoteActionCardPureProps = {
  /** Write ONE on-camera script for the optional teleprompter. Absent → the rep can still type one. */
  onGenerateVideoScript?: () => Promise<string>;
  /** Upload + send the take. Resolve to close the card; reject to keep the take for a retry. */
  onSendVideo: (video: Blob, durationMs: number, script: string) => Promise<void>;
  /** Camera strategy for surfaces where `getUserMedia` needs help (the extension). */
  mediaStrategy?: VideoMediaStrategy;
};

/**
 * "Send a video note": a plain video recorder. Opening the card turns the camera on — record, watch it
 * back, retake or send as a LinkedIn video message. A script is optional: "Show script" writes one and
 * scrolls it on a teleprompter while recording. No AI avatar or voice clone — it is the rep, recorded.
 */
export function VideoNoteActionCardPure({ onGenerateVideoScript, onSendVideo, mediaStrategy }: VideoNoteActionCardPureProps) {
  const [open, setOpen] = React.useState(false);
  const [showScript, setShowScript] = React.useState(false);
  const [scriptRaw, setScriptRaw] = React.useState('');
  const [isWriting, setIsWriting] = React.useState(false);
  const [scriptError, setScriptError] = React.useState<string | null>(null);
  // Bumped on reset so the recorder remounts (fresh camera, no stale take) the next time the card opens.
  const [sessionKey, setSessionKey] = React.useState(0);

  // Anything HTML-shaped is flattened to plain spoken text (same defence as the voice card).
  const setScript = (value: string) =>
    setScriptRaw(/<\w+[^>]*>|&\w+;/.test(value) ? getTextFromHtml(value, true) : value);
  const script = showScript ? scriptRaw : '';

  const writeScript = async () => {
    if (!onGenerateVideoScript) return;
    setIsWriting(true);
    setScriptError(null);
    try {
      const next = (await onGenerateVideoScript()).trim();
      if (next) setScript(next);
      else setScriptError('Nothing came back. Try again, or type your own.');
    } catch {
      setScriptError("Couldn't write a script. Try again, or type your own.");
    } finally {
      setIsWriting(false);
    }
  };

  const toggleScript = () => {
    const next = !showScript;
    setShowScript(next);
    // Write one only when asked for, and only once — editing or re-opening keeps the rep's text.
    if (next && !scriptRaw.trim() && onGenerateVideoScript) void writeScript();
  };

  const reset = React.useCallback(() => {
    setOpen(false);
    setShowScript(false);
    setScriptRaw('');
    setScriptError(null);
    setSessionKey((k) => k + 1);
  }, []);

  useEscStack('VIDEO_NOTE_CARD', ESC_PRIORITY.MED, open ? reset : undefined);

  if (!open) {
    return (
      <Button
        className="w-full"
        color="blue"
        tone="soft"
        onClick={() => setOpen(true)}
        icon={<StepIconSvg iconType="video_note" />}
        data-testid="video-note-open">
        <span>
          Send a{' '}
          <span className="font-medium text-blue-700 underline decoration-blue-300/50 underline-offset-2 group-hover/btn:text-white group-hover/btn:decoration-white/60 dark:text-blue-300 dark:decoration-blue-500/50">
            Video Note
          </span>
        </span>
      </Button>
    );
  }

  return (
    <div
      className="flex flex-col overflow-hidden rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-950"
      data-testid="video-note-card">
      <div className="flex items-center gap-2 px-3 py-2">
        <Images.Camera01 className="size-5 shrink-0 text-red-600" />
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-astronaut-700 dark:text-astronaut-300">
          Video note
        </span>
        <Button
          size="xs"
          color="gray"
          tone="outline"
          active={showScript}
          onClick={toggleScript}
          tooltip={showScript ? 'Hide the teleprompter' : 'Show a script on a teleprompter while you record'}
          data-testid="video-note-script-toggle">
          {showScript ? 'Hide script' : 'Show script'}
        </Button>
        <Button
          size="sm"
          text
          color="gray"
          tooltip="Cancel"
          onClick={reset}
          data-testid="video-note-cancel"
          icon={<General.XMark className="size-4" />}
        />
      </div>

      {showScript && (
        <div className="flex flex-col gap-1 px-3 pb-2" data-testid="video-note-script-panel">
          {isWriting ? (
            <p className="py-2 text-xs text-gray-500 dark:text-gray-400" data-testid="video-note-script-writing">
              Writing a script…
            </p>
          ) : (
            <Textarea
              value={scriptRaw}
              onChange={(e) => setScript(e.target.value)}
              placeholder="Type what to say — it scrolls on the teleprompter while you record."
              rows={3}
              color="red"
              tone="outline"
              className="w-full"
              data-testid="video-note-script"
            />
          )}
          {scriptError != null && <p className="text-xs text-red-600 dark:text-red-400">{scriptError}</p>}
          {onGenerateVideoScript != null && !isWriting && (
            <Button
              size="xs"
              text
              color="blue"
              className="self-start"
              onClick={() => void writeScript()}
              data-testid="video-note-rewrite">
              {scriptRaw.trim() ? 'Write a different one' : 'Write one for me'}
            </Button>
          )}
        </div>
      )}

      <div className="px-3 pb-3">
        <VideoRecorder
          key={sessionKey}
          script={script}
          autoOpen
          mediaStrategy={mediaStrategy}
          onCancel={reset}
          onSend={async (video, durationMs) => {
            await onSendVideo(video, durationMs, script.trim());
            reset();
          }}
        />
      </div>
    </div>
  );
}
